import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/setup/redux/RootReducer';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import * as area from '../redux/AreaRedux';
import { getAreas, createArea } from '../redux/AreaCRUD';
import { LabeledField } from '@/__core/components/LabeledField';
import AddButtonSelect from '@/__core/hocs/AddButtonSelect';
import { MessageSnackbar } from '@/__core/helpers/components';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';

interface AreaSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  labelWidth?: number;
}

export default function AreaSelect({
  value,
  onChange,
  label = "Khu vực",
  placeholder = "Chọn khu vực",
  disabled = false,
  error = false,
  helperText,
  required = false,
  labelWidth,
}: AreaSelectProps) {
  const dispatch = useDispatch();
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  
  // State for notifications
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Redux selectors
  const areas = useSelector((state: RootState) => state.areas.areas);
  const areasLoading = useSelector((state: RootState) => state.areas.loading);

  // Handle adding new area
  const handleAddNewArea = async (newArea: { name: string }) => {
    try {
      setErrorMessage(null);
      startGlobalLoading('Đang tạo khu vực mới...', 'area-create');
      const response = await createArea({
        name: newArea.name,
        description: "",
        is_active: true,
      });
      
      // Refresh areas list to get the updated list with the new area
      const updatedResponse = await getAreas();
      const areasData = updatedResponse.data.results || updatedResponse.data || [];
      dispatch(area.actions.fulfillAreas(areasData));
      
      // Find the newly created area from the updated list
      const newAreaFromList = areasData.find((area: any) => 
        area.name === newArea.name
      );
      
      // Show success message
      setSuccessMessage(`Khu vực "${newArea.name}" đã được tạo thành công!`);
      
      if (newAreaFromList) {
        return {
          id: newAreaFromList.id.toString(),
          name: newAreaFromList.name,
        };
      } else {
        // Fallback: return the name as both id and name
        return {
          id: newArea.name,
          name: newArea.name,
        };
      }
    } catch (error: any) {
      console.error("Error creating area:", error);
      setErrorMessage(formatErrorForSnackbar(error) || "Có lỗi xảy ra khi tạo khu vực mới");
      throw error;
    } finally {
      stopGlobalLoading('area-create');
    }
  };

  return (
    <>
      <LabeledField label={label} required={required} labelWidth={labelWidth}>
        <AddButtonSelect
          options={(areas || []).map(area => ({
            id: area.id.toString(),
            name: area.name,
          }))}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          disabled={disabled || areasLoading}
          placeholder={areasLoading ? "Đang tải..." : placeholder}
          onAddNew={handleAddNewArea}
          addDialogTitle="Thêm khu vực mới"
          addDialogLabel="Tên khu vực"
          addDialogPlaceholder="Nhập tên khu vực"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3b82f6',
                borderWidth: '2px',
              },
            },
          }}
        />
      </LabeledField>

      {/* Success Snackbar */}
      <MessageSnackbar
        open={!!successMessage}
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Error Snackbar */}
      <MessageSnackbar
        open={!!errorMessage}
        message={errorMessage}
        severity="error"
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
}
